import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useMemo, useState } from 'react';
import 'ag-grid-enterprise';
import { scrapeDataConfig } from '../_utils/scrapeData-config';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  downloadJobData,
  getAllScrapedJobs,
} from '../../../services/apiService';
import BackArrowIcon from '../../../icons/BackArrowIcon';

import {
  useLocation,
  useHistory,
} from 'react-router-dom/cjs/react-router-dom.min';
import './ScrapeData.css';
import SQLInputWithSuggestions from './SqlLikeFilter';
import { toast } from 'react-toastify';
import LoadingCircleIcon from '../../../icons/LoadingCircle';
import ViewRowData from './ViewRowData';

const pageSize = 10;

const ScrapeData = () => {
  let lastRequestedPage = null;
  const [dateRange, setDateRange] = useState('last24hours');
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [customTimeRange, setCustomTimeRange] = useState({
    startTime: '00:00',
    endTime: '23:59',
  });
  const [gridApi, setGridApi] = useState(null);
  const [filters, setFilters] = useState({});
  const [sqlQuery, setSqlQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentRowData, setCurrentRowData] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const selectedIdsParam = searchParams.get('selectedIds'); // "1,2"
  const selectedIds = selectedIdsParam
    ? selectedIdsParam.split(',').map(Number)
    : [];

  const jobData = location.state?.jobData;
  console.log(jobData, isOpen, pageNumber, setPageNumber, currentPage, 'hob');

  const [startDate, endDate] = customDateRange;

  const agGridColumnDefs = useMemo(() => {
    return scrapeDataConfig({
      setCurrentRowData,
      setIsOpen,
      isOpen,
    }).map((colDef) => ({
      ...colDef,
      sortable: colDef.field !== 'description' && colDef.field !== 'skills', // Disable sorting for description and job skills
      // You can also set specific sort comparators if needed
      // comparator: customComparator, // Optional custom sort function
    }));
  }, []);

  const handleQueryChange = (query) => {
    setSqlQuery(query); // Save query for Apply Filter
  };

  const handleTimeChange = (field, value) => {
    setCustomTimeRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDownloadFile = async () => {
    setIsLoading(true);
    try {
      const response = await downloadJobData({
        filters: {
          whereClause: filters.whereClause || '',
          startDate: filters.startDate || '',
          endDate: filters.endDate || '',
        },
        relevant: 'selected',
        selectedIds,
      });
      
      const blob = new Blob([response], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'job-data.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    } finally {
      setIsLoading(false);
    }
  };

  const getServerSideDatasource = () => {
    return {
      getRows: async (params) => {
        const { startRow, sortModel } = params.request;
        const page = Math.floor(startRow / pageSize) + 1;

        if (page === lastRequestedPage) {
          return;
        }

        lastRequestedPage = page;

        // Prepare sort parameters
        const sortData = [];
        if (sortModel && sortModel.length > 0) {
          sortData.push([
            sortModel[0].colId,
            sortModel[0].sort.toUpperCase(),
          ]);
        }

        // Get current filter state
        const currentFilter = JSON.parse(localStorage.getItem('jobDataFilter') || '{}');
        
        // Remove whereClause if it's empty or undefined
        if (!currentFilter.whereClause || currentFilter.whereClause.trim() === '') {
          delete currentFilter.whereClause;
        }

        const payload = {
          selectedIds,
          page: page,
          filters: currentFilter,
          ...(sortData.length > 0 && { order: sortData }),
        };

        try {
          const response = await getAllScrapedJobs(payload);
          const totalCount = response?.data?.totalCount;
          params.successCallback(response?.data?.data || [], totalCount);
        } catch (err) {
          toast.error('Failed to fetch data');
          params.failCallback();
        }
      },
    };
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    const datasource = getServerSideDatasource();
    params.api.setServerSideDatasource(datasource);

    // Add pagination event listener
    params.api.addEventListener('paginationChanged', (event) => {
      const currentPage = params.api.paginationGetCurrentPage();
      setCurrentPage(currentPage);
      setPageNumber(currentPage + 1);
    });

    // Add sort changed event listener
    params.api.addEventListener('sortChanged', (event) => {
      console.log('Sort changed');
      // Reset to first page when sorting changes
      lastRequestedPage = null;
      setCurrentPage(0);
      setPageNumber(1);

      // Create new datasource with current filter
      const newDatasource = getServerSideDatasource();
      params.api.setServerSideDatasource(newDatasource);
    });
  };

  const toISTISOString = (date) => {
    const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
    const istDate = new Date(date.getTime() + istOffset);
    return istDate.toISOString();
  };

  const combineDateAndTime = (date, timeString) => {
    if (!date || !timeString) return null;

    const [hours, minutes] = timeString.split(':').map(Number);
    const combinedDate = new Date(date);
    combinedDate.setHours(hours, minutes, 0, 0);

    return combinedDate;
  };

  const handleApplyFilter = () => {
    console.log('apply filter called');
    const now = new Date();
    const appliedFilter = {};

    // Only add whereClause if sqlQuery is not empty
    if (sqlQuery && sqlQuery.trim()) {
      appliedFilter.whereClause = sqlQuery.trim();
    }

    if (dateRange === 'custom' && startDate && endDate) {
      const startDateTime = combineDateAndTime(
        startDate,
        customTimeRange.startTime
      );
      const endDateTime = combineDateAndTime(endDate, customTimeRange.endTime);

      if (startDateTime && endDateTime) {
        appliedFilter.startDate = toISTISOString(startDateTime);
        appliedFilter.endDate = toISTISOString(endDateTime);
      }
    } else {
      let start, end;

      switch (dateRange) {
        case 'last1hours':
          end = new Date();
          start = new Date(end.getTime() - 1 * 60 * 60 * 1000);
          break;
        case 'last4hours':
          end = new Date();
          start = new Date(end.getTime() - 4 * 60 * 60 * 1000);
          break;
        case 'last8hours':
          end = new Date();
          start = new Date(end.getTime() - 8 * 60 * 60 * 1000);
          break;
        case 'last24hours':
          end = new Date();
          start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
          break;
        case 'yesterday':
          start = new Date();
          start.setDate(now.getDate() - 1);
          start.setHours(0, 0, 0, 0);
          end = new Date(start);
          end.setHours(23, 59, 59, 999);
          break;
        case 'last7days':
          start = new Date();
          start.setDate(now.getDate() - 6);
          start.setHours(0, 0, 0, 0);
          end = new Date();
          end.setHours(23, 59, 59, 999);
          break;
        case 'thisMonth':
          start = new Date(now.getFullYear(), now.getMonth(), 1);
          start.setHours(0, 0, 0, 0);
          end = new Date();
          end.setHours(23, 59, 59, 999);
          break;
        default:
          start = null;
          end = null;
      }

      if (start && end) {
        appliedFilter.startDate = toISTISOString(start);
        appliedFilter.endDate = toISTISOString(end);
      }
    }

    // Only save to localStorage if we have filters
    if (Object.keys(appliedFilter).length > 0) {
      // Remove whereClause if it's empty
      if (!appliedFilter.whereClause || appliedFilter.whereClause.trim() === '') {
        delete appliedFilter.whereClause;
      }
      localStorage.setItem('jobDataFilter', JSON.stringify(appliedFilter));
      setFilters(appliedFilter);
    } else {
      // Clear filters if none are applied
      localStorage.removeItem('jobDataFilter');
      setFilters({});
    }

    if (gridApi) {
      // Reset to first page when applying new filter
      lastRequestedPage = null;
      setCurrentPage(0);
      setPageNumber(1);

      // Create new datasource with updated filter
      const newDatasource = getServerSideDatasource();
      gridApi.setServerSideDatasource(newDatasource);
    }
  };

  const handleCreateSuccess = (pagecurrent) => {
    if (gridApi) {
      // First set the page
      gridApi.paginationGoToPage(pagecurrent);

      // Then refresh the data
      const datasource = getServerSideDatasource();
      gridApi.setServerSideDatasource(datasource);

      // Ensure the page is maintained after refresh
      setTimeout(() => {
        gridApi.paginationGoToPage(pagecurrent);
      }, 100);
    }
  };

  // Load saved filter on component mount
  useEffect(() => {
    const savedFilter = JSON.parse(localStorage.getItem('jobDataFilter') || '{}');
    if (Object.keys(savedFilter).length > 0) {
      setFilters(savedFilter);
      setSqlQuery(savedFilter.whereClause || '');
    }
  }, []);

  useEffect(() => {
    handleApplyFilter();
  }, []);

  return (
    <div className='scrape-data-container'>
      <div className='filter-section'>
        <div className='flex items-center gap-2'>
          <button
            onClick={() => history.goBack()}
            className='bg-gray-200 font-semibold hover:bg-gray-300 text-black px-3 py-1 rounded flex items-center justify-center w-10 h-10'
            aria-label='Go back'
          >
            <BackArrowIcon />
          </button>
          <div className='text-2xl font-semibold'>Job Listings</div>
        </div>
        <div className='flex justify-between items-center mt-4 flex-wrap'>
          <div className='flex items-center gap-4 flex-wrap'>
            <span>Date Filter</span>
            <select
              onChange={(e) => setDateRange(e.target.value)}
              className='border px-2 py-1 rounded cursor-pointer'
              value={dateRange}
            >
              <option value='last1hours'>Last 1 Hours</option>
              <option value='last4hours'>Last 4 Hours</option>
              <option value='last8hours'>Last 8 Hours</option>
              <option value='last24hours'>Last 24 Hours</option>
              <option value='yesterday'>Yesterday</option>
              <option value='last7days'>Last 7 Days</option>
              <option value='thisMonth'>This Month</option>
              <option value='custom'>Custom Date Range</option>
            </select>

            {dateRange === 'custom' && (
              <div className='flex items-center gap-2 flex-wrap'>
                <DatePicker
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => setCustomDateRange(update)}
                  isClearable
                  placeholderText='Select custom date range'
                  className='border px-2 py-1 pr-10 rounded w-52 z-50'
                />

                <div className='flex items-center gap-2'>
                  <span className='text-sm font-medium'>From:</span>
                  <input
                    type='time'
                    value={customTimeRange.startTime}
                    onChange={(e) =>
                      handleTimeChange('startTime', e.target.value)
                    }
                    className='border px-2 py-1 rounded text-sm'
                  />
                </div>

                <div className='flex items-center gap-2'>
                  <span className='text-sm font-medium'>To:</span>
                  <input
                    type='time'
                    value={customTimeRange.endTime}
                    onChange={(e) =>
                      handleTimeChange('endTime', e.target.value)
                    }
                    className='border px-2 py-1 rounded text-sm'
                  />
                </div>
              </div>
            )}

            <button
              onClick={handleApplyFilter}
              className='bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700 transition'
            >
              Apply Date Filter
            </button>
            <button
              onClick={handleApplyFilter}
              className='bg-purple-600 text-white px-4 py-1 rounded hover:bg-purple-700 transition'
            >
              Apply Query Filter
            </button>
          </div>

          <div className='mt-2 md:mt-0'>
            <button
              onClick={handleDownloadFile}
              disabled={isLoading}
              className={`flex items-center justify-center px-4 py-1 rounded transition text-white min-w-[140px]
    ${
      isLoading
        ? 'bg-gray-400 cursor-not-allowed'
        : 'bg-green-600 hover:bg-green-700'
    }`}
            >
              {isLoading ? (
                <>
                  <LoadingCircleIcon iconStyle='w-5 h-5 mr-2 animate-spin' />
                </>
              ) : (
                'Download File'
              )}
            </button>
          </div>
        </div>

        <div className='mt-2'>
          <SQLInputWithSuggestions onQueryChange={handleQueryChange} />
        </div>
      </div>
      <div className='flex-1 overflow-hidden'>
        <div
          className='ag-theme-alpine h-full w-full'
          style={{ height: '95%' }}
        >
          <AgGridReact
            columnDefs={agGridColumnDefs}
            onGridReady={onGridReady}
            tooltipShowDelay={500}
            tooltipHideDelay={2000}
            enableBrowserTooltips={true}
            rowHeight={50}
            domLayout='normal'
            rowModelType='serverSide'
            serverSideStoreType='partial'
            animateRows={true}
            serverSideInfiniteScroll={true}
            pagination={true}
            paginationPageSize={pageSize}
            cacheBlockSize={pageSize}
            maxBlocksInCache={1}
            sortingOrder={['asc', 'desc', null]}
            multiSortKey='ctrl'
            suppressRowClickSelection={true}
            enableCellTextSelection={true}
            allowContextMenuWithControlKey={true}
            suppressContextMenu={false}
            suppressDragLeaveHidesColumns={true}
            suppressColumnVirtualisation={true}
            suppressRowVirtualisation={true}
          />
        </div>
        {isOpen && (
          <ViewRowData
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            currentRowData={currentRowData}
            setCurrentRowData={setCurrentRowData}
            gridApi={gridApi}
            handleCreateSuccess={handleCreateSuccess}
            setPageNumber={setPageNumber}
            pageNumber={pageNumber}
          />
        )}
      </div>
    </div>
  );
};

export default ScrapeData;
